/**
 * Four small desk tools the support, moderation and flag screens were missing (queue item 14).
 *
 * - **PanditReplies** (admin doc 20) — a pandit's reply waits for moderation like a review does.
 *   Each is shown beside the review it answers, because "does this disclose the devotee" is only
 *   decidable next to what she wrote.
 * - **InternalTesters** (doc 27) — the allow-list is replaced whole. The editor holds a draft of
 *   the list and sends all of it; removing somebody is sending the list without them.
 * - **DisputeEvidenceLink** (doc 18 §3.2) — support cites a completion photo, a ticket attachment
 *   or a note of a call. It is filed as the platform's, never as either party's words.
 * - **PhoneIntake** (doc 21 §3.3) — a ticket opened for somebody who rang. The caller is bound
 *   first, by a lookup that shows masked identities only; nothing submits without one.
 */
import { useState } from 'react';
import { Note, Rating } from '../components/primitives';
import { Input, Select } from '../components/forms';
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { useModerateResponse, useReviewResponses } from '../lib/api/hooks/reviews';
import { useInternalTesters, useSetInternalTesters } from '../lib/api/hooks/flags';
import { useLinkDisputeEvidence, usePhoneIntake, type AdminTicketRow } from '../lib/api/hooks/support';
import { useUsers, type UserRow } from '../lib/api/hooks/users';

/* ──────────────────────────────  Pandit replies  ────────────────────────────── */

export function PanditReplies() {
  const { scope, toast } = useConsole();
  const replies = useReviewResponses();
  const moderate = useModerateResponse();
  const decide = (id: number, decision: 'publish' | 'reject') =>
    moderate.mutate(
      { id, decision },
      {
        onSuccess: () => toast({ tone: 'success', message: decision === 'publish' ? 'Reply published' : 'Reply rejected' }),
        onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
      },
    );

  return (
    <Section title="Pandit replies awaiting moderation" note="oldest first · a reply may never disclose the devotee">
      {replies.isPending && <LoadingState message="Reading the replies…" />}
      {replies.isError && <ErrorState title="Could not read the replies" cause={errorMessage(replies.error)} />}
      {replies.isSuccess && replies.data.items.length === 0 && (
        <EmptyState inline title="No reply waiting" body="Replies arrive here as pandits answer published reviews." />
      )}
      {replies.isSuccess && replies.data.items.length > 0 && (
        <DataTable
          density="compact"
          columns={[
            { key: 'review', label: 'The review' },
            { key: 'reply', label: 'The reply' },
            { key: 'act', label: '' },
          ]}
          rows={replies.data.items.map((r) => ({
            id: r.id,
            review: (
              <span style={{ display: 'grid', gap: 'var(--sp-xs)' }}>
                <Rating value={r.review_rating} />
                <span>{r.review_body ?? 'A rating only'}</span>
              </span>
            ),
            reply: r.body,
            act: (
              <span style={{ display: 'flex', gap: 'var(--sp-xs)' }}>
                <ScopeAction scope={scope} need="support" size="sm" onClick={() => decide(r.id, 'publish')}>
                  Publish
                </ScopeAction>
                <ScopeAction scope={scope} need="support" size="sm" variant="secondary" onClick={() => decide(r.id, 'reject')}>
                  Reject
                </ScopeAction>
              </span>
            ),
          }))}
        />
      )}
    </Section>
  );
}

/* ──────────────────────────────  Internal testers  ────────────────────────────── */

export function InternalTesters() {
  const { scope, toast } = useConsole();
  const testers = useInternalTesters();
  const save = useSetInternalTesters();
  // `null` is "the list as the server has it"; an edit copies it into a draft.
  const [draft, setDraft] = useState<number[] | null>(null);
  const [adding, setAdding] = useState('');

  const current = testers.data?.items.map((t) => t.user_id) ?? [];
  const list = draft ?? current;
  const notes = new Map(testers.data?.items.map((t) => [t.user_id, t.note]) ?? []);
  const addError = adding === '' || /^[1-9]\d*$/.test(adding) ? undefined : 'A user number';
  const canAdd = adding !== '' && !addError && !list.includes(Number(adding));

  return (
    <Section title="Internal testers" note="replaced whole · somebody removed stops seeing unreleased surfaces at once">
      {testers.isPending && <LoadingState message="Reading the list…" />}
      {testers.isError && <ErrorState title="Could not read the list" cause={errorMessage(testers.error)} />}
      {testers.isSuccess && (
        <>
          {list.length === 0 ? (
            <EmptyState inline title="Nobody on the list" body="An internal-only flag reaches nobody until somebody is added." />
          ) : (
            <DataTable
              density="compact"
              columns={[
                { key: 'user', label: 'User', mono: true },
                { key: 'note', label: 'Note' },
                { key: 'act', label: '' },
              ]}
              rows={list.map((id) => ({
                id,
                user: id,
                note: notes.get(id) || (current.includes(id) ? '—' : 'not saved yet'),
                act: (
                  <ScopeAction
                    scope={scope}
                    need="superadmin"
                    size="sm"
                    variant="ghost"
                    onClick={() => setDraft(list.filter((other) => other !== id))}
                  >
                    Remove
                  </ScopeAction>
                ),
              }))}
            />
          )}
          <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', marginTop: 'var(--sp-md)' }}>
            <Input label="Add a user number" inputMode="numeric" value={adding} onChange={setAdding} error={addError} />
            <ScopeAction
              scope={scope}
              need="superadmin"
              size="sm"
              variant="secondary"
              disabled={!canAdd}
              onClick={() => {
                setDraft([...list, Number(adding)]);
                setAdding('');
              }}
            >
              Add
            </ScopeAction>
            <ScopeAction
              scope={scope}
              need="superadmin"
              size="sm"
              disabled={draft === null || save.isPending}
              onClick={() =>
                save.mutate(list, {
                  onSuccess: (saved) => {
                    setDraft(null);
                    toast({ tone: 'success', message: `Tester list saved — ${saved.items.length} on it` });
                  },
                  onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
                })
              }
            >
              Save list
            </ScopeAction>
            {draft !== null && (
              <ScopeAction scope={scope} need="superadmin" size="sm" variant="ghost" onClick={() => setDraft(null)}>
                Discard changes
              </ScopeAction>
            )}
          </div>
        </>
      )}
    </Section>
  );
}

/* ──────────────────────────────  Dispute evidence  ────────────────────────────── */

const EVIDENCE_KINDS = ['A note of a call or message', 'A completion photo', 'A ticket attachment'] as const;

export function DisputeEvidenceLink({ code }: { code: string }) {
  const { scope, toast } = useConsole();
  const link = useLinkDisputeEvidence();
  const [kind, setKind] = useState<string>(EVIDENCE_KINDS[0]);
  const [value, setValue] = useState('');
  const isNote = kind === EVIDENCE_KINDS[0];
  const idError = isNote || value === '' || /^[1-9]\d*$/.test(value) ? undefined : 'An id number';
  const valid = isNote ? value.trim().length >= 3 : value !== '' && !idError;

  return (
    <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'end', flexWrap: 'wrap', marginTop: 'var(--sp-md)' }}>
      <Select
        label="Link"
        value={kind}
        options={EVIDENCE_KINDS}
        onChange={(next) => {
          setKind(next);
          setValue('');
        }}
      />
      {isNote ? (
        <Input label="Note" value={value} onChange={setValue} helper="e.g. phone call 2026-08-21 18:05 IST — what was said" />
      ) : (
        <Input label={kind === EVIDENCE_KINDS[1] ? 'Photo id' : 'Attachment id'} inputMode="numeric" value={value} onChange={setValue} error={idError} />
      )}
      <ScopeAction
        scope={scope}
        need="support"
        size="sm"
        disabled={!valid || link.isPending}
        onClick={() =>
          link.mutate(
            isNote
              ? { code, kind: 'external_note', note: value.trim() }
              : kind === EVIDENCE_KINDS[1]
                ? { code, kind: 'completion_evidence', completion_evidence_id: Number(value) }
                : { code, kind: 'ticket_attachment', ticket_attachment_id: Number(value) },
            {
              onSuccess: () => {
                setValue('');
                toast({ tone: 'success', message: 'Linked to the dispute' });
              },
              onError: (e) => toast({ tone: 'error', message: errorMessage(e) }),
            },
          )
        }
      >
        Link it
      </ScopeAction>
    </div>
  );
}

/* ──────────────────────────────  Phone intake  ────────────────────────────── */

/** The server's closed set (`TicketCategory`). */
const CATEGORIES = [
  'booking_issue',
  'payment_issue',
  'refund_status',
  'pandit_conduct',
  'devotee_conduct',
  'safety',
  'account_access',
  'app_technical',
  'content_correction',
  'kyc_onboarding',
  'payout_query',
  'other',
] as const;
const PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;

function callerLabel(user: UserRow): string {
  return [user.masked_name, user.masked_phone, user.masked_email].filter(Boolean).join(' · ');
}

/** A booking's public code: `BMP` and nine letters or digits (CHAR(12), R65). The server drops a
 *  code it cannot find rather than refusing it, so a typo would open a ticket about no booking. */
export function bookingCodeError(raw: string): string | undefined {
  const code = raw.trim().toUpperCase();
  return code === '' || /^BMP[0-9A-Z]{9}$/.test(code) ? undefined : 'BMP and nine letters or digits';
}

export function PhoneIntake({
  onDone,
  boundTo,
}: {
  onDone: (opened?: AdminTicketRow) => void;
  /** Opened from a profile (admin doc 22 §3.2 "Open ticket"): the caller is already known. */
  boundTo?: UserRow;
}) {
  const { scope, toast } = useConsole();
  const intake = usePhoneIntake();
  const [lookup, setLookup] = useState('');
  const [caller, setCaller] = useState<UserRow | null>(boundTo ?? null);
  const [category, setCategory] = useState<string>('booking_issue');
  const [priority, setPriority] = useState<string>('normal');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [booking, setBooking] = useState('');
  const query = lookup.trim();
  // Nothing is read until the agent has typed: a page of callers nobody asked for is still data.
  const matches = useUsers({ page: 1, page_size: 5, q: query }, query !== '');
  // The service forces it too; the form says so rather than letting the agent pick "low".
  const safety = category === 'safety';
  const bookingError = bookingCodeError(booking);
  const valid = caller !== null && subject.trim() !== '' && body.trim() !== '' && !bookingError;

  return (
    <Section title="Phone intake" note="a ticket for somebody who rang · channel is phone">
      <Note>Find the caller first. Only masked details are shown — confirm who they are by asking, not by reading out.</Note>
      {caller === null ? (
        <div style={{ display: 'grid', gap: 'var(--sp-sm)', marginTop: 'var(--sp-md)' }}>
          <Input label="Caller (phone, email or name)" value={lookup} onChange={setLookup} />
          {matches.isSuccess && matches.data.items.length === 0 && (
            <EmptyState inline title="Nobody matches" body="Check the number with the caller." />
          )}
          {matches.isSuccess &&
            matches.data.items.map((user) => (
              <div key={user.id} style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'center' }}>
                <span style={{ fontSize: 'var(--fs-admin-ui)' }}>{callerLabel(user)}</span>
                <ScopeAction scope={scope} need="support" size="sm" variant="secondary" onClick={() => setCaller(user)}>
                  This is the caller
                </ScopeAction>
              </div>
            ))}
          {matches.isError && <ErrorState inline title="Could not search" cause={errorMessage(matches.error)} />}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 'var(--sp-sm)', marginTop: 'var(--sp-md)' }}>
          <div style={{ display: 'flex', gap: 'var(--sp-md)', alignItems: 'center' }}>
            <span style={{ fontSize: 'var(--fs-admin-ui)' }}>Caller: {callerLabel(caller)}</span>
            <ScopeAction scope={scope} need="support" size="sm" variant="ghost" onClick={() => setCaller(null)}>
              Change caller
            </ScopeAction>
          </div>
          <Select label="Category" value={category} options={CATEGORIES} onChange={setCategory} />
          {safety ? (
            <Note>A safety ticket is always urgent, whatever the caller says.</Note>
          ) : (
            <Select label="Priority" value={priority} options={PRIORITIES} onChange={setPriority} />
          )}
          <Input label="Subject" value={subject} onChange={setSubject} />
          <Input label="What they said" value={body} onChange={setBody} multiline />
          <Input label="Booking (BMP code)" value={booking} onChange={setBooking} helper="Optional" error={bookingError} />
        </div>
      )}
      {intake.isError && <ErrorState inline title="The ticket was not opened" cause={errorMessage(intake.error)} />}
      <div style={{ display: 'flex', gap: 'var(--sp-sm)', marginTop: 'var(--sp-md)' }}>
        <ScopeAction
          scope={scope}
          need="support"
          size="sm"
          disabled={!valid || intake.isPending}
          onClick={() =>
            intake.mutate(
              {
                on_behalf_of_user_id: caller!.id,
                channel: 'phone',
                category,
                priority: safety ? 'urgent' : priority,
                subject: subject.trim(),
                body: body.trim(),
                booking_code: booking.trim().toUpperCase() || null,
              },
              {
                onSuccess: (ticket) => {
                  toast({ tone: 'success', message: `Opened ${ticket.public_code} — read it back to the caller` });
                  onDone(ticket);
                },
              },
            )
          }
        >
          Open ticket
        </ScopeAction>
        <ScopeAction scope={scope} need="support" size="sm" variant="ghost" onClick={() => onDone()}>
          Cancel
        </ScopeAction>
      </div>
    </Section>
  );
}
